import { RadioOptionType } from 'components/RadioButton/RadioButton';
import React, { ReactElement, ReactNode } from 'react';

interface MylerTestimonial {
  testimonial?: string[];
  name: string;
  tier: string;
  year: string;
}
interface MylerTool {
  name: string;
  description: string;
}

interface MylerResource {
  name: string;
  blocks: Array<string>;
}

export type MylerFAQ = {
  title: string;
  text: string | React.ReactNode;
  expanded: boolean;
};

const NavMenus: Array<string> = ['About', 'Why MyLER', 'Enquire', 'FAQs'];

const MylerTools: Array<MylerTool> = [
  {
    name: 'Technology ',
    description:
      'Our cutting-edge technology equips you with tools designed to increase efficiency and scalability, whilst streamlining your business processes, freeing up your time to focus on creativity, learning, and delivering outstanding service.',
  },
  {
    name: 'Knowledge',
    description:
      "Through our comprehensive tutorials, virtual and in-person training sessions, detailed onboarding, ongoing support, and extensive product insights, you'll be equipped with the expertise and confidence needed to excel with your clients.",
  },
  {
    name: 'Relationships',
    description:
      'With over a decade of experience, we’ve built exceptional partnerships with over 5,000 luxury hotels and travel suppliers, whilst cultivating a supportive community among MyLERs, ensuring you thrive within our network.',
  },
];

const MylerResources: Array<MylerResource> = [
  {
    name: 'Booking platform',
    blocks: [
      'Our fully GDS-integrated booking platform allows you to search and book effortlessly, whether on our app or website. Accessible via laptop, phone, or tablet, it offers the flexibility to manage your business seamlessly—anytime, anywhere.',
    ],
  },
  {
    name: 'Comprehensive content',
    blocks: [
      'Dive into detailed hotel and room information, from floorplans and videos to Instagram integrations, fun facts, and USPs. This rich content ensures you deliver bespoke recommendations, perfectly tailored to your clients’ needs.',
    ],
  },
  {
    name: 'Consortia',
    blocks: [
      'We’re proud members of Virtuoso, granting you access to elite offers and VIP benefits at over 2,000 luxury providers worldwide. From iconic hotels to cruise lines, tour operators and more, this partnership ensures you can offer your clients unforgettable experiences.',
    ],
  },
  {
    name: 'Commissions',
    blocks: [
      'Stay informed with estimated earnings visible on every booking, so that you know exactly what you’ll earn. Our intuitive dashboard provides a clear view of what you’ve earned, due payments and pipeline. Plus, we handle commission collection for you, with seamless monthly payments.',
    ],
  },
  {
    name: 'Industry access',
    blocks: [
      'Meet and exceed our thresholds to unlock invitations to prestigious industry events, trade shows, and familiarisation trips. You’ll also enjoy MyLER-specific incentives, recognising and rewarding your hard work, whilst fostering growth and expanding your luxury travel network.',
    ],
  },
  {
    name: 'Community',
    blocks: [
      'Benefit from a dedicated community manager to guide your onboarding, training, and professional development. Whether answering questions or sharing insights, they’ll help you maximize your potential.',
    ],
  },
  {
    name: 'Little Black Book',
    blocks: [
      'Our dynamic directory features a range of travel suppliers, resources and contacts utilising our dynamic search for you to find what you need in one place. With this at your fingertips, securing bookings and connecting with key contacts has never been easier.',
    ],
  },
  {
    name: 'Marketing toolkit',
    blocks: [
      'Elevate your brand with our MyLER marketing toolkit, designed to help you create branded proposals, access premium content, and leverage a library of social media assets to effectively promote your business.',
    ],
  },
];

const MylerTestimonials: MylerTestimonial[] = [
  {
    name: 'Annabelle',
    tier: 'MyLER',
    year: '2022',
    testimonial: [
      `I have been a MyLER for 18 months and it’s been the perfect job for me, giving me the autonomy to work at times and days that are convenient for me. One of the best parts has been getting to know the Little Emperors team, every single member of which will go above and beyond to help you achieve your potential. Their platform is so simple to use and there is always someone willing to assist if you need help.`,
      `I feel so lucky to be a MyLER as it’s truly a welcoming and wonderful company in so many ways. Rebecca is a natural leader, driving Little Emperors and harvesting a community spirit among the entire team. I’m excited to watch the company continue to grow as they have so many amazing new developments coming soon. I have no doubt Little Emperors will be the only place you’ll want to go to book your next holiday.`,
    ],
  },
  {
    name: 'Stefan',
    tier: 'MyLER',
    year: '2022',
    testimonial: [
      `The Little Emperors Myler’s platform is the most forward thinking that I have seen in terms of usability for ICs in the travel industry. It provides a powerful booking tool that allows you to search, book and manage clients reservations efficiently. Their technology is backed up by an expert travel team which are very attentive to helping you achieve the best for your clients when extra support is needed offline. Complimenting their online portal, their app has enabled me to easily send quotes out digitally to clients whilst on the go, freeing up my time that would have initially been spent formatting different quotes manually on just a desktop platform in an office.`,
      `Thank you.`,
    ],
  },
  {
    name: 'Tristan',
    tier: 'MyLER',
    year: '2022',
    testimonial: [
      `Joining LE has been an exceptional experience for me, especially as an American and first time travel advisor. I was searching for a highly boutique and customer-centric company and LE truly delivers. The MyLER IC program is a seamless, technology-driven program with an extensive network of preferred partner hotels, special rates and benefits- all extremely accessible in just a few clicks. The ability to quickly pull organized PDFs for clients on the go has been life changing. The LE team is highly responsive and always looking to add new qualified partners as well as improve upon all aspects of the operation.`,
    ],
  },
];

const MylerFAQs: MylerFAQ[] = [
  {
    expanded: true,
    title: 'What is a MyLER?',
    text: `A MyLER is an Independent Travel Agent powered by Little Emperors. Little
    Emperors have been in the business for over 13 years, creating proprietary hotel
    booking technology and the Little Emperors app as well as building key industry relationships.
    Little Emperors extends its knowledge and tools to a network of Independent Travel
    Agents, empowering them to earn an income through booking travel.`,
  },
  {
    expanded: false,
    title:
      'What’s the difference between Independent Travel Agents, Independent Travel Advisors and MyLERS?',
    text: `They are all different terms for the same thing; an individual who runs their own business booking travel on behalf of others – at Little Emperors, we call them MyLERs. As a MyLER, you get the added perks of being affiliated to Little Emperors, something that not all Independent Travel Agents benefit from.`,
  },
  {
    expanded: false,
    title: 'Will I be an employee of Little Emperors?',
    text: `As an Independent Travel Agent, you won’t be a Little Emperors employee. Instead, you will be operating your own business with Little Emperors as your affiliate partner, giving you maximum control and flexibility.`,
  },
  {
    expanded: false,
    title: 'What does an Independent Travel Advisor do?',
    text: `You’ll build a network of clients and book their travel. Via the Little Emperors app, you’ll have all the information you need to provide detailed proposals within seconds from anywhere in the world and book and manage your client’s trips. You may choose to just book hotel stays, or you may prefer to create whole travel itineraries for your clients.`,
  },
  {
    expanded: false,
    title:
      'Where do Independent Travel Agents work from, and how many hours will I work?',
    text: `The beauty of the MyLER programme is everything is online, so you can work from
    anywhere in the world. As this is your business, you can work whatever hours work
    for you.`,
  },
  {
    expanded: false,
    title: 'How much money can I earn as an Independent Travel Agent?',
    text: `The sky’s the limit when it comes to an Independent Travel Agents’ income – the
    more you book, the more you’ll earn. Earnings are not capped, so you can decide
    how much work you want to do to determine your earnings.`,
  },
  {
    expanded: false,
    title: 'How will I be paid?',
    text: `After your booking has checked out, we will collect the commission payments on your
    behalf and send you a booking report. We then simply require an invoice from you
    and once received we will pay you by direct bank transfer.`,
  },
  {
    expanded: false,
    title: 'What are the benefits of being an Independent Travel Agent?',
    text: `As we’ve already mentioned, you have maximum flexibility and full control over your
    schedule, your earning potential is not capped as an Independent Travel Agent.
    What we didn’t mention is you’ll also become part of the best industry. You’ll know
    about the best hotels in the world, network with hoteliers and be able to provide the
    best hotel perks.`,
  },
  {
    expanded: false,
    title: 'Why MyLER and why Little Emperors?',
    text: `Simply put, we have the best Independent Travel Agent programme out there. Little
    Emperors tech is unrivalled, providing in one place all the content you need and a
    seamless booking platform. After 13 years, we have the best relationships with the
    best hotels ensuring you won’t find our preferential rates and benefits elsewhere and
    you’ll have access to all the key industry events.`,
  },
  {
    expanded: false,
    title: 'Can Independent Travel Agents use Little Emperors branding?',
    text: `You absolutely can, and you can have a Little Emperors email address too. If you
    would prefer to use your own brand, we support that too, and you can even attach
    your own logo to the PDF proposals.`,
  },
  {
    expanded: false,
    title:
      'Can I become a MyLER even though I am already an Independent Travel Agent?',
    text: `Absolutely. We love to provide Independent Travel Agents who are already up and
    running with extra tools to help them grow their business.`,
  },
  {
    expanded: false,
    title: 'Can I only book hotels?',
    text: `While we offer the best hotels and benefits, our 'Little Black Book' provides access to
    additional preferred travel suppliers, covering flights, cruises, DMCs, and more. You
    can also use our IATA number to book hotels we don’t work with and still collect
    commission. We give you access to everything you need to be a full-service
    Independent Travel Agent.`,
  },
  {
    expanded: false,
    title: 'Do I have to handle payments?',
    text: `Our rates are set up so that the client pays directly to the hotel, meaning you don’t
    have to collect payment.`,
  },
];

export const InformationSourceOptions = [
  {
    name: 'Advertisement',
    value: 'Advertisement',
  },
  {
    name: 'Event or Trade Show',
    value: 'Event or Trade Show',
  },
  {
    name: 'Internet',
    value: 'Internet',
  },
  {
    name: 'Referral',
    value: 'Referral',
  },
  {
    name: 'Social media',
    value: 'Social media',
  },
  {
    name: 'Travel Forums or Communities',
    value: 'Travel Forums or Communities',
  },
  {
    name: 'Other',
    value: 'Other',
  },
];

export const IATAOptions: RadioOptionType[] = [
  {
    label: 'IATA',
    value: 'iata',
  },
  {
    label: 'TIDS',
    value: 'tids',
  },
];
export { NavMenus, MylerTools, MylerResources, MylerTestimonials, MylerFAQs };
